import { NextElement, PauseButton, PlayButton, PreviousElement } from "@assets";
import { LFlex } from "@components";
import { useState, useEffect, useRef } from "react";

export interface Track {
    url: string;
    targetWord?: string;
}

const prettyMMSS = (seconds: number): string => {
    if (isNaN(seconds)) {
        return "0:00";
    }

    return new Date(seconds * 1000).toISOString().slice(14, 19);
};

const AudioPlayer = ({ tracks, stepTitle, onTrackPlayed, startingIndex = 0 }: AudioPlayerProps): JSX.Element => {
    const [trackIndex, setTrackIndex] = useState(startingIndex);
    const [trackProgress, setTrackProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const audioSrc = tracks[trackIndex];

    const audioRef = useRef(new Audio(audioSrc.url));
    const intervalRef = useRef<NodeJS.Timer>();
    const isReady = useRef(false);

    const { duration } = audioRef.current;

    const currentPercentage = duration ? `${(trackProgress / Math.floor(duration)) * 100}%` : "0%";
    const trackStyling = `
    -webkit-gradient(linear, 0% 0%, 100% 0%, color-stop(${currentPercentage}, #fff), color-stop(${currentPercentage}, #777))
  `;

    const startTimer = (): void => {
        // Clear any timers already running
        clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            setTrackProgress(Math.ceil(audioRef.current.currentTime));
        }, 1000);
    };

    const onScrub = (value: number): void => {
        // Clear any timers already running
        clearInterval(intervalRef.current);
        audioRef.current.currentTime = value;
        setTrackProgress(Math.ceil(audioRef.current.currentTime));
    };

    const onScrubEnd = (): void => {
        // If not already playing, start
        if (!isPlaying) {
            setIsPlaying(true);
        }
        startTimer();
    };

    const toPrevTrack = (): void => {
        if (trackIndex - 1 < 0) {
            setTrackIndex(tracks.length - 1);
        } else {
            setTrackIndex(trackIndex - 1);
        }
    };

    const toNextTrack = (): void => {
        if (trackIndex < tracks.length - 1) {
            setTrackIndex(trackIndex + 1);
        } else {
            setTrackIndex(0);
        }
    };

    const togglePlayPause = (): void => {
        setIsPlaying(!isPlaying);
    };

    useEffect(() => {
        if (isPlaying) {
            void audioRef.current.play();
            onTrackPlayed?.(trackIndex);
            startTimer();
        } else {
            audioRef.current.pause();
        }
    }, [isPlaying]);

    // Handles cleanup and setup when changing tracks
    useEffect(() => {
        audioRef.current.pause();

        audioRef.current = new Audio(audioSrc.url);
        setTrackProgress(Math.ceil(audioRef.current.currentTime));

        if (isReady.current) {
            void audioRef.current.play();
            setIsPlaying(true);
            startTimer();
        } else {
            // Set the isReady ref as true for the next pass
            isReady.current = true;
        }
    }, [trackIndex]);

    useEffect(() => {
        // Pause and clean up on unmount
        return () => {
            audioRef.current.pause();
            clearInterval(intervalRef.current);
        };
    }, [tracks]);

    return (
        <LFlex row alignCenter justifyCenter>
            <a onClick={toPrevTrack}>
                <PreviousElement />
            </a>
            <LFlex className="has-text-white mx-6 mb-4">
                <p className="has-text-weight-bold is-size-6">Playing</p>
                {audioSrc.targetWord && <p className="has-text-weight-bold is-size-4">{audioSrc.targetWord}</p>}
                <p className={`has-text-weight-bold ${audioSrc.targetWord ? "is-size-6" : "is-size-4"}`}>{stepTitle}</p>
            </LFlex>

            <a onClick={togglePlayPause}>{!isPlaying ? <PlayButton /> : <PauseButton />}</a>
            <LFlex className="mx-6 mb-4">
                <LFlex row alignCenter className="is-justify-content-space-between has-text-white">
                    <p>{prettyMMSS(trackProgress)}</p>
                    <p>{prettyMMSS(Math.round(duration))}</p>
                </LFlex>
                <input
                    type="range"
                    value={trackProgress}
                    step={1}
                    min={0}
                    max={Math.ceil(duration) || `${duration}`}
                    onChange={(e) => onScrub(Number(e.target.value))}
                    onMouseUp={onScrubEnd}
                    onKeyUp={onScrubEnd}
                    style={{ background: trackStyling, width: "35vw" }}
                />
            </LFlex>
            <a onClick={toNextTrack}>
                <NextElement />
            </a>
        </LFlex>
    );
};

interface AudioPlayerProps {
    tracks: Track[];
    stepTitle: string;
    startingIndex?: number;
    onTrackPlayed: (trackIndex: number) => void;
}

export default AudioPlayer;
